/**
 * Feature-flag helpers (frontend-only, localStorage-backed).
 *
 * The IR pipeline (utils/ir/*) is being rolled in behind a flag while we
 * verify geometric parity with the legacy Sugiyama layout (utils/dagLayout.js).
 * This module is the single source of truth for whether the new pipeline is
 * active at runtime.
 *
 * USAGE
 * ─────
 *   import { isIRPipelineEnabled } from '../utils/featureFlags';
 *   const positioned = isIRPipelineEnabled()
 *     ? layoutDAGviaIR(nodes, connections, opts)
 *     : layoutDAG(nodes, connections, opts);
 *
 * TOGGLING
 * ────────
 * In a browser console:
 *   localStorage.setItem('features.irPipeline', 'true');  // enable
 *   localStorage.removeItem('features.irPipeline');       // back to default
 *
 * Tests can call `setFeatureFlag('irPipeline', true)` to opt-in for a
 * single test case without poking localStorage themselves.
 *
 * SERVER-SIDE / NODE TEST SAFETY
 * ──────────────────────────────
 * All accessors defensively guard `window` / `localStorage` so this module
 * can be imported from Jest/Node environments that lack them.
 */

const STORAGE_PREFIX = 'features.';

/** In-memory fallback for test environments that don't expose localStorage. */
const memoryStore = new Map();

function hasLocalStorage() {
  try {
    return typeof window !== 'undefined' && window.localStorage != null;
  } catch (_) {
    return false;
  }
}

function readFlag(name) {
  const key = STORAGE_PREFIX + name;
  if (hasLocalStorage()) {
    try {
      const v = window.localStorage.getItem(key);
      if (v !== null) return v;
    } catch (_) {
      // fall through to memory
    }
  }
  return memoryStore.has(key) ? memoryStore.get(key) : null;
}

function writeFlag(name, value) {
  const key = STORAGE_PREFIX + name;
  if (value == null) {
    memoryStore.delete(key);
    if (hasLocalStorage()) {
      try { window.localStorage.removeItem(key); } catch (_) {}
    }
    return;
  }
  const str = value === true ? 'true' : value === false ? 'false' : String(value);
  memoryStore.set(key, str);
  if (hasLocalStorage()) {
    try { window.localStorage.setItem(key, str); } catch (_) {}
  }
}

/**
 * True iff the flag is explicitly set to the string 'true'. Any other value
 * (including `null`, `'false'`, `'0'`) resolves false.
 *
 * @param {string} name  Bare flag name (the `features.` prefix is added).
 * @returns {boolean}
 */
export function getFeatureFlag(name) {
  return readFlag(name) === 'true';
}

/**
 * Read a flag that defaults ON: returns true when the flag is unset (or 'true'),
 * and false ONLY when it has been explicitly Force-OFF'd ('false'). Used by the
 * default-on flags (redesign foundations, the rebirth V2 shell + pages) so a
 * clean boot lands on the shipped V2 experience while explicit opt-outs persist.
 */
export function isFlagDefaultOn(name) {
  return readFlag(name) !== 'false';
}

/**
 * Set (or clear) a feature flag. Pass `null`/`undefined` to reset to default.
 *
 * @param {string} name
 * @param {boolean|null} value
 */
export function setFeatureFlag(name, value) {
  writeFlag(name, value);
  // Keep <html data-redesign> in sync the moment a palette flag is toggled,
  // instead of waiting for the next full page boot. (applyRedesignPalette is a
  // hoisted function declaration defined below.)
  if (typeof name === 'string' && name.startsWith('redesign.')) applyRedesignPalette();
}

// ───────────────────────────────────────────────────────────────────
//  Named flags — keep a short, explicit list
// ───────────────────────────────────────────────────────────────────

/**
 * True iff the IR pipeline (flatToIR → layoutIR → irToFlat → routeEdges
 * with measured-height relayout) should drive canvas layout.
 *
 * DEFAULT: FALSE (reverted from R3-H's default-ON). The IR pipeline passed
 * every unit-level geometry diff (task #117 side-by-side, task #118 height-
 * feedback) but still produces catastrophically-overlapping container chrome
 * on real production workflows — the 29-node `complex_claim_stress_test`
 * canvas shipped with a FORK at x=-135 width=1667 bleeding clear outside its
 * enclosing SWITCH at x=4 width=1679. Root cause: `layoutIR` sizes containers
 * bottom-up from subtree footprint with no negotiation for sibling horizontal
 * budget or trunk anchoring, so nested containers routinely exceed their
 * parent's inner width. Fixing this properly requires a full hierarchical
 * layout engine rather than more patches on the hand-rolled Sugiyama.
 *
 * GOING FORWARD: Canvas V2 (see `isCanvasV2Enabled()` below and tasks
 * #159–#163) replaces both the legacy Sugiyama and the IR pipeline with
 * `elkjs` hierarchical `elk.layered` layout + `@xyflow/react` rendering.
 * V2 is built in parallel behind its own flag; the IR pipeline stays in
 * the tree as a reference (and remains opt-in via the override below)
 * until V2 reaches parity on all five container constructs.
 *
 * EXPLICIT OPT-IN (for A/B or bisect):
 *   localStorage.setItem('features.irPipeline', 'true');
 *
 * @returns {boolean}
 */
export function isIRPipelineEnabled() {
  const raw = readFlag('irPipeline');
  // Default: OFF. Explicit 'true' opts into the IR pipeline for bisecting.
  if (raw === null) return false;
  return raw === 'true';
}

/**
 * True iff Canvas V2 (the `@xyflow/react` + `elkjs` hierarchical rewrite
 * at `/canvas-v2`) should be available at runtime. Canvas V2 is the long-
 * term replacement for both the legacy Sugiyama in `utils/dagLayout.js`
 * and the IR pipeline in `utils/ir/*`.
 *
 * When OFF (default): the `/canvas-v2` route is unregistered and the
 * legacy `/canvas` page is the only path. Users see no behaviour change.
 *
 * When ON: both `/canvas` (legacy) and `/canvas-v2` (new) are registered
 * in `App.jsx`. No production nav surface links to V2 yet — testers reach
 * it by typing the URL directly. This keeps the blast radius of a broken
 * V2 build near zero while we iterate on Switch → Fork → DynamicFork →
 * SubWorkflow → DoWhile parity (tasks #160–#162).
 *
 * Licensing sanity: `@xyflow/react` is MIT, `elkjs` is EPL-2.0 — both
 * cleared for commercial redistribution.
 *
 * Toggle in browser console:
 *   localStorage.setItem('features.canvasV2', 'true');   // enable V2 route
 *   localStorage.removeItem('features.canvasV2');        // back to default
 *
 * @returns {boolean}
 */
export function isCanvasV2Enabled() {
  return getFeatureFlag('canvasV2');
}

/**
 * True iff Canvas V3 (the legacy /canvas chrome with V2's hierarchical
 * ReactFlow + ELK DAG renderer swapped into the centre) should be
 * available at runtime. V3 is the bridge build that lets us cut
 * /canvas → V3 in a single line once it's signed off (task #182).
 *
 * When OFF (default): the `/canvas-v3` route is unregistered. /canvas
 * (legacy) and /canvas-v2 (V2 demo harness) remain untouched.
 *
 * When ON: /canvas-v3 mounts alongside the others. No nav surface links
 * to it yet — testers reach it by typing the URL directly. Same blast-
 * radius posture as canvasV2 had during its early iteration.
 *
 * Toggle in browser console:
 *   localStorage.setItem('features.canvasV3', 'true');   // enable V3 route
 *   localStorage.removeItem('features.canvasV3');        // back to default
 *
 * @returns {boolean}
 */
export function isCanvasV3Enabled() {
  return getFeatureFlag('canvasV3');
}

/**
 * True iff the next-generation /canvas-v4 surface is enabled. V4 is the
 * strangler-fig replacement for V3 — it reuses the proven state machine
 * (useCanvas), layout pipeline (flatToElkHierarchical), picker, and palette,
 * but ships with a fraction of V3's 16k-line host shell and with the
 * Round 1 patches (drag persistence, manual edge draw, per-branch +,
 * undo/redo, copy/paste, multi-select with visual highlight) all designed
 * natively rather than retrofit.
 *
 * Phase 1 (current): /canvas-v4 route mounts behind this flag. Shell + minimal
 * DAG render. Phases 2-4 add insertion flows, undo/redo, beta polish.
 *
 * Default OFF. Enable in browser console for testing:
 *   localStorage.setItem('features.canvasV4', 'true');   // enable V4
 *   localStorage.removeItem('features.canvasV4');        // back to default OFF
 *
 * The /canvas (V3) route is unaffected by this flag — V4 lives in parallel
 * at /canvas-v4 until the cutover criteria are met (full feature parity
 * for the common case, byte-for-byte save round-trip validated against V3,
 * 2-week internal beta with no regressions).
 *
 * @returns {boolean}
 */
export function isCanvasV4Enabled() {
  return getFeatureFlag('canvasV4');
}

/**
 * True iff the /canvas route should render CanvasPageV4 INSTEAD OF
 * CanvasPageV3 as the default. This is the route-flip flag — when ON, V4
 * becomes the primary canvas surface for everyone; when OFF (default),
 * /canvas continues to render V3 and V4 lives parallel at /canvas-v4.
 *
 * Cutover criteria (see docs/CANVAS_V4_PARITY_AUDIT.md):
 *   • Phase 4d audit Steps 1-4 all pass for the production tenant.
 *   • 2-week internal beta with no V4-only console errors.
 *   • Telemetry shows ≥95% of common-case insertions complete successfully
 *     in V4 (matching V3's success rate).
 *
 * Rollback plan: if a regression surfaces post-flip, unset this flag —
 * /canvas immediately returns to V3 with no data loss (V4 and V3 share
 * the same workflow schema, so any V4-saved workflow loads cleanly in V3).
 *
 * Default OFF. /canvas-v3 remains mounted permanently as escape hatch
 * regardless of this flag's value — pinned bookmarks stay stable.
 *
 * Toggle in browser console:
 *   localStorage.setItem('features.canvasV4Default', 'true');   // flip to V4
 *   localStorage.removeItem('features.canvasV4Default');        // back to V3
 *
 * NOTE: this flag presupposes isCanvasV4Enabled() — V4's parallel route
 * must be enabled before it can be promoted to default.
 *
 * @returns {boolean}
 */
export function isCanvasV4DefaultEnabled() {
  return isCanvasV4Enabled() && getFeatureFlag('canvasV4Default');
}

/**
 * True iff the right-panel "Code" tab (Conductor OSS-parity full-workflow JSON
 * editor, bidirectional paste-to-render via the IR pipeline) should be
 * shown. Defaults TRUE once stable — we ship behind a flag so QA can
 * A/B the tab-strip header change on day one.
 *
 * Toggle in browser console:
 *   localStorage.setItem('features.codeTab', 'true');   // explicit on
 *   localStorage.setItem('features.codeTab', 'false');  // explicit off
 *   localStorage.removeItem('features.codeTab');        // follow default
 *
 * @returns {boolean}
 */
export function isCodeTabEnabled() {
  const raw = readFlag('codeTab');
  if (raw === null) return true; // default ON — the Code tab is the headline UX of #136
  return raw === 'true';
}

/**
 * True iff the new inline Task Picker (CANVAS_PICKER_INLINE_DESIGN.md) is
 * mounted in place of the legacy 8-shortcut popup on the Canvas. The flag
 * lets us land Phases 1→6 of the redesign incrementally without exposing
 * half-built UI to production tenants.
 *
 * Default rollout posture:
 *   • dev / localhost / staging:  ON  — testers see the new picker by default
 *   • production:                 OFF — flips to ON once Phase 6 (polish +
 *                                       a11y + telemetry) lands.
 *
 * The "dev" check uses NODE_ENV !== 'production' so CRA's `start` script
 * (development build) defaults the flag on. Any explicit setting in
 * localStorage wins over the default in either direction, which is what
 * QA needs for A/B verification on a single browser.
 *
 * Toggle in browser console:
 *   localStorage.setItem('features.canvasPicker', 'true');   // explicit on
 *   localStorage.setItem('features.canvasPicker', 'false');  // explicit off
 *   localStorage.removeItem('features.canvasPicker');        // follow default
 *
 * @returns {boolean}
 */
export function isCanvasPickerEnabled() {
  // RC-14 (task #194) — Click-to-add via the inline TaskPicker is now the
  // ONLY insertion path on V3. Drag-and-drop has been surgically removed
  // (it was the source of a chain of regressions where nodes spliced into
  // wrong slots and silent failures left workflows half-built). The flag
  // is retained for explicit OFF override via localStorage so QA can A/B
  // emergency-disable the picker UI, but the default is now unconditionally
  // ON — production included — because the alternative is no insertion
  // path at all post-RC-14.
  const raw = readFlag('canvasPicker');
  if (raw === null) return true;
  return raw === 'true';
}

/**
 * RC-18e (task #203) — V4 UX restructure flag.
 *
 * <p>When OFF (default in every environment) V3 renders with its existing
 * toolbar, right rail (Workflow / Task / Code tabs), and execution
 * panel — zero change from pre-RC-18 behavior. When ON, V3 routes those
 * surfaces through V4's components (V4Toolbar / V4RightRail with its
 * single-mode strip / V4BottomDock for run-time).</p>
 *
 * <p>This is the structural tier of the V4 enhancements port and has
 * non-trivial visual disruption potential — gating behind explicit
 * opt-in is the only way to honor the operator's "no regression"
 * directive while still making the new UX available for evaluation.
 * Set {@code localStorage.features.v4UX = "true"} to enable.</p>
 *
 * <p>Adoption path: ship Tier 1 + Tier 2 first (purely additive, on
 * everywhere). Operators evaluate. If approved, default-flip this flag
 * to true; if rejected, the V4 imports stay dormant and can be removed
 * without consequence.</p>
 *
 * @returns {boolean}
 */
export function isV4UXEnabled() {
  const raw = readFlag('v4UX');
  if (raw === null) return false;  // default OFF — opt-in only
  return raw === 'true';
}

/**
 * True iff the container-model rework (R3 — task #137) should be honored.
 *
 * When ON:
 *  - The backend converter's typed `_containerChildren` hint (see
 *    `CONTAINER_CHILDREN_HINT` in `utils/ir/containerHint.js`) is read by
 *    `flatToIR` to nest children inside their owning SWITCH / FORK /
 *    FORK_JOIN_DYNAMIC / DO_WHILE / SUB_WORKFLOW container.
 *  - Nested children are removed from the top-level peer list and become
 *    `container.cases[].children` / `container.branches[].children` /
 *    `container.body[]` / `container.embedded[]`.
 *  - The renderer applies kind-specific container chrome (see
 *    `applyContainerSizing` in `utils/dagLayout.js`, generalized from
 *    `applyDoWhileContainerSizing`).
 *  - `canvas.loadCanvasData` and the Code tab serializer preserve
 *    container nesting round-trip.
 *
 * When OFF (default for now): the existing flat-peer emission path is
 * used end-to-end. Zero behavior change until the flag is flipped.
 *
 * Toggle in browser console:
 *   localStorage.setItem('features.containerModel', 'true');
 *   localStorage.removeItem('features.containerModel');
 *
 * Rollout plan: see docs/R3_CONTAINER_MODEL_REWORK.md §merge-gates.
 *
 * @returns {boolean}
 */
/**
 * True iff the Agentlets-managed worker hosting (Temporal OSS-backed) toggle
 * is exposed in the right-panel Worker tab. Default OFF — the surface is
 * a placeholder ahead of the backend Temporal OSS-pool work. When OFF,
 * `worker_task` nodes still work fully against the Conductor-style
 * polling story (the default `hostingMode: 'conductor_poll'`) — the flag
 * only controls visibility of the Hosting Mode toggle in the UI.
 *
 * Toggle in browser console:
 *   localStorage.setItem('features.workerHosting', 'true');
 *   localStorage.removeItem('features.workerHosting');
 *
 * @returns {boolean}
 */
export function isWorkerHostingEnabled() {
  return readFlag('workerHosting') === 'true';
}

export function isContainerModelEnabled() {
  return getFeatureFlag('containerModel');
}

// ───────────────────────────────────────────────────────────────────
//  Rebirth (UI v2) — phase rollout flags
//  ──────────────────────────────────────────────────────────────────
//  Each phase of the redesigned UX (see Agentlets-Implementation-Plan.md
//  in /Users/abrahams/Product) ships behind its own flag. The shell flag
//  must be ON for any per-page flag to render its v2 component; the
//  composition rule lives in App.jsx and DashboardLayout.jsx.
//
//  Naming convention:
//      features.rebirth.shell.v2          ← Phase 0 (this PR)
//      features.rebirth.overview.v2       ← Phase 1
//      features.rebirth.approvals.v2      ← Phase 2
//      ... etc.
//
//  Toggle in browser console (or via /flags admin page):
//      localStorage.setItem('features.rebirth.shell.v2', 'true');
//      localStorage.removeItem('features.rebirth.shell.v2');
// ───────────────────────────────────────────────────────────────────

/**
 * True iff the Rebirth UX foundation is enabled — exposes /v2-playground
 * (Phase 0) and, in later phases, mounts the redesigned shell + page
 * components for the user.
 *
 * Default: OFF. Internal dogfood only until Phase 0 acceptance, then
 * SuperAdmin-flippable via /flags, then per-tenant rollout from Phase 1.
 *
 * @returns {boolean}
 */
export function isRebirthShellEnabled() {
  return isFlagDefaultOn('rebirth.shell.v2');
}

/**
 * True iff the redesigned Overview page (Phase 1) should render.
 *
 * COMPOSITION RULE (Implementation-Plan §5.3): the Overview v2 only
 * activates when BOTH the shell flag AND this per-page flag are on.
 * That keeps the v1 page visible inside the v2 shell for users who
 * have opted into the new chrome but haven't yet been rolled into
 * the new Overview cohort.
 *
 * Default: OFF.
 *
 * @returns {boolean}
 */
export function isRebirthOverviewEnabled() {
  return isRebirthShellEnabled() && isFlagDefaultOn('rebirth.overview.v2');
}

/**
 * True iff the redesigned Approvals queue (Phase 2) should render.
 * Composition rule applies — requires the shell flag too.
 * @returns {boolean}
 */
export function isRebirthApprovalsEnabled() {
  return isRebirthShellEnabled() && isFlagDefaultOn('rebirth.approvals.v2');
}

/**
 * True iff the redesigned Agentlets inventory (Phase 3) should render.
 * Composition rule applies — requires the shell flag too.
 * @returns {boolean}
 */
export function isRebirthAgentletsInventoryEnabled() {
  return isRebirthShellEnabled() && isFlagDefaultOn('rebirth.agentlets-inventory.v2');
}

/**
 * True iff the redesigned Agentlet Detail page (Phase 4) should render.
 * Single flag controls all three sub-phases (4a Build / 4b Decide / 4c Act);
 * sub-phase scope is gated within the page itself by capability checks.
 * Composition rule applies.
 * @returns {boolean}
 */
export function isRebirthAgentletDetailEnabled() {
  return isRebirthShellEnabled() && isFlagDefaultOn('rebirth.agentlet-detail.v2');
}

/**
 * True iff the redesigned Discovery pages (Phase 5) should render.
 * Single flag covers Agent Discovery (formerly Shadow Discovery) and
 * Workflow Discovery. Composition rule applies.
 * @returns {boolean}
 */
export function isRebirthDiscoveryEnabled() {
  return isRebirthShellEnabled() && isFlagDefaultOn('rebirth.discovery.v2');
}

/**
 * True iff the redesigned Governance Center (Phase 6) should render.
 * Single flag covers the landing page + Trust / Compliance / Data Flow /
 * Audit Trail / Workflow Intelligence sub-views. Composition rule applies.
 * @returns {boolean}
 */
export function isRebirthGovernanceEnabled() {
  return isRebirthShellEnabled() && isFlagDefaultOn('rebirth.governance.v2');
}

/* ─── Phases 7-11 page-level rebirth flags ──────────────────── */
export function isRebirthPoliciesEnabled()       { return isRebirthShellEnabled() && isFlagDefaultOn('rebirth.policies.v2'); }
export function isRebirthSkillCenterEnabled()    { return isRebirthShellEnabled() && isFlagDefaultOn('rebirth.skill-center.v2'); }
export function isRebirthBuildSectionEnabled()   { return isRebirthShellEnabled() && isFlagDefaultOn('rebirth.build-section.v2'); }
export function isRebirthAdministerEnabled()     { return isRebirthShellEnabled() && isFlagDefaultOn('rebirth.administer.v2'); }
export function isRebirthSystemEnabled()         { return isRebirthShellEnabled() && isFlagDefaultOn('rebirth.system.v2'); }

// ───────────────────────────────────────────────────────────────────
//  Generic helpers for nav-section and AI-feature flags.
// ───────────────────────────────────────────────────────────────────

/**
 * Read a nav-section flag (`features.nav.<key>`). Defaults to TRUE when
 * unset — the platform's nav is fully visible by default; a SuperAdmin
 * has to explicitly hide a section to flip it off. Used by
 * DashboardLayout.jsx to filter the sidebar.
 *
 * @param {string} key e.g. 'operations', 'governance', 'intelligence'
 * @returns {boolean}
 */
export function isNavSectionEnabled(key) {
  if (!key) return true;
  const raw = readFlag(`nav.${key}`);
  if (raw === null) return true; // default ON for every nav section
  return raw === 'true';
}

/**
 * Read an AI-feature flag (`features.ai.<key>`). Defaults to TRUE when
 * unset so every shipped AI capability is on out of the box; flipping
 * one off lets a tenant disable that specific feature for compliance
 * or budget reasons (e.g. turn off `ai.policyExtractor` when running
 * an air-gapped instance).
 *
 * @param {string} key e.g. 'canvasBuilder', 'explainerPanel', 'recommendationChips'
 * @returns {boolean}
 */
export function isAIFeatureEnabled(key) {
  if (!key) return true;
  const raw = readFlag(`ai.${key}`);
  if (raw === null) return true; // default ON for every AI feature
  return raw === 'true';
}

// ───────────────────────────────────────────────────────────────────
//  Registry — powers the /flags admin page (SuperAdmin-only).
//  Each entry declares:
//   - name        : bare flag key (the `features.` prefix is added)
//   - label       : short human name
//   - description : one-paragraph UI summary
//   - defaultOn   : what the flag evaluates to when unset
//   - accessor    : the public getter (so the page shows the *resolved*
//                   value, including per-flag default rules)
//
//  The page uses this list to render toggles generically — new flags
//  auto-appear here by being added to the array, not by touching UI code.
// ───────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} FeatureFlagDescriptor
 * @property {string}   name
 * @property {string}   label
 * @property {string}   description
 * @property {boolean}  defaultOn
 * @property {() => boolean} accessor
 * @property {string}   [group]            — optional UI grouping, used by /flags admin page
 * @property {boolean}  [backendManaged]   — true means the flag lives in
 *                                            application.yml / env vars and
 *                                            cannot be toggled from the UI; the
 *                                            FlagRow renders informational
 *                                            (property name + default + link).
 * @property {string}   [propertyName]     — Spring property key, e.g.
 *                                            "agentlets.workflow-ai.enabled"
 * @property {string}   [defaultValue]     — string-formatted default
 *                                            (only used when backendManaged)
 * @property {string}   [restartImpact]    — short note on what restarting
 *                                            means for this flag
 * @property {boolean}  [serverToggle]     — backend-managed BOOLEAN with
 *                                            Save ON/OFF/Default controls that
 *                                            persist a server override
 * @property {boolean}  [serverText]       — backend-managed STRING value; renders
 *                                            a text field + Save/Reset instead of a
 *                                            boolean toggle (ServerTextFlagRow)
 * @property {string}   [flagKey]          — server runtime-flag key persisted via
 *                                            /api/v1/platform/runtime-flags
 * @property {string}   [placeholder]      — input placeholder (serverText flags)
 */

// Helper builder for the nav / AI flag families so the registry stays
// readable. Resolves to a stable accessor that delegates to the generic
// getter functions defined above.
const navAccessor = (key) => () => isNavSectionEnabled(key);
const aiAccessor  = (key) => () => isAIFeatureEnabled(key);

/* ───────────────────────────────────────────────────────────────────────────
 *  Redesign palette (brand alignment)
 *  ──────────────────────────────────────────────────────────────────────────
 *  Three mutually-exclusive palette presets re-bind the --v2-* tokens (see
 *  styles/redesign-themes.css) by setting <html data-redesign="…">. Each is a
 *  opt-in flag — EXCEPT foundations (Redesign · A), which defaults ON as the
 *  shipped brand palette. When more than one is on, precedence is
 *  dark > foundations > trust. applyRedesignPalette() runs on boot
 *  (src/index.js), on every redesign.* toggle (setFeatureFlag), and on every
 *  route change (App.jsx) so a long-lived SPA session can't drift back to the
 *  default palette.
 * ────────────────────────────────────────────────────────────────────────── */
export function isRedesignFoundationsEnabled() {
  const raw = readFlag('redesign.foundations');
  if (raw === null) return true; // default ON — brand foundations (Redesign · A) is the shipped palette
  return raw === 'true';
}
export function isRedesignDarkEnabled()        { return getFeatureFlag('redesign.dark'); }
export function isRedesignTrustEnabled()        { return getFeatureFlag('redesign.trust'); }

/** @returns {'dark'|'foundations'|'trust'|null} the winning palette, or null. */
export function getActiveRedesignPalette() {
  if (isRedesignDarkEnabled())        return 'dark';
  if (isRedesignFoundationsEnabled()) return 'foundations';
  if (isRedesignTrustEnabled())        return 'trust';
  return null;
}

/** Reflect the active redesign palette onto <html data-redesign>. Safe in
 *  non-DOM (test) environments. */
export function applyRedesignPalette() {
  if (typeof document === 'undefined' || !document.documentElement) return;
  const palette = getActiveRedesignPalette();
  if (palette) document.documentElement.setAttribute('data-redesign', palette);
  else document.documentElement.removeAttribute('data-redesign');
}

/** @type {FeatureFlagDescriptor[]} */
export const FLAG_REGISTRY = [
  // ── Canvas / layout ──────────────────────────────────────────────
  {
    name: 'canvasV2',
    label: 'Canvas V2 (ReactFlow + ELK hierarchical)',
    description:
      'Enables the /canvas-v2 route — the @xyflow/react + elkjs hierarchical ' +
      'layout rewrite that replaces the legacy Sugiyama and the IR pipeline. ' +
      'When OFF the route is unregistered entirely. No nav surface links to ' +
      'V2 yet; testers reach it by typing /canvas-v2 directly.',
    defaultOn: false,
    accessor: isCanvasV2Enabled,
    group: 'Canvas & layout',
  },
  {
    name: 'canvasV3',
    label: 'Canvas V3 (legacy chrome + V2 DAG)',
    description:
      'Enables the /canvas-v3 route — the legacy /canvas left palette + top ' +
      'action bar + right Workflow/Task/Code panels with the V2 hierarchical ' +
      'ELK + ReactFlow DAG renderer swapped into the centre. Bridge build for ' +
      'the final cutover (#175). Mounts alongside /canvas and /canvas-v2 ' +
      'while we validate end-to-end. Testers reach it by typing /canvas-v3.',
    defaultOn: false,
    accessor: isCanvasV3Enabled,
    group: 'Canvas & layout',
  },
  {
    name: 'irPipeline',
    label: 'IR pipeline (legacy path)',
    description:
      'Routes /canvas layout through flatToIR → layoutIR → irToFlat instead ' +
      'of the hand-rolled Sugiyama in utils/dagLayout.js. Default OFF since ' +
      'task #159 — the pipeline shipped geometrically-broken container chrome ' +
      'on real workflows. Left available for bisecting and A/B comparison ' +
      'until Canvas V2 reaches parity on all five container constructs.',
    defaultOn: false,
    accessor: isIRPipelineEnabled,
    group: 'Canvas & layout',
  },
  {
    name: 'codeTab',
    label: 'Right-panel Code tab',
    description:
      'Shows the full-workflow JSON editor on the right side of /canvas — ' +
      'bidirectional, debounced parse, re-renders the canvas via ' +
      'loadCanvasData. This is the headline UX of task #136 and the primary ' +
      'way to paste a workflow into V2 during the parity migration.',
    defaultOn: true,
    accessor: isCodeTabEnabled,
    group: 'Canvas & layout',
  },
  {
    name: 'canvasPicker',
    label: 'Inline Task Picker (Phase 1+)',
    description:
      'Mounts the new inline Task Picker (CANVAS_PICKER_INLINE_DESIGN.md) ' +
      'in place of the legacy 8-shortcut popup that opens when the user ' +
      'clicks "+" on a connection. Tab strip, search, recommended-here, ' +
      'recipes, full taxonomy. Lands incrementally across Phases 1-6. ' +
      'Defaults ON in dev/test, OFF in production until Phase 6 ships.',
    defaultOn: true, // dev/test default; prod default is computed at runtime
    accessor: isCanvasPickerEnabled,
    group: 'Canvas & layout',
  },
  {
    name: 'containerModel',
    label: 'Nested container model (R3)',
    description:
      'Honors the backend converter\'s _containerChildren hint so SWITCH / ' +
      'FORK / FORK_JOIN_DYNAMIC / DO_WHILE / SUB_WORKFLOW children nest ' +
      'inside their parent container instead of flat-promoting. When OFF, ' +
      'the flat-peer emission path is used end-to-end (zero behaviour change).',
    defaultOn: false,
    accessor: isContainerModelEnabled,
    group: 'Canvas & layout',
  },

  // ── Rebirth (UI v2) — phase rollout ─────────────────────────────
  // The redesigned UX rolls out phase by phase behind flags. Phase 0
  // (this entry) gates the v2 design system foundation: the
  // /v2-playground reference page, the v2 component library, and
  // (in later phases of Phase 0) the new shell. Per-page flags
  // (rebirth.overview.v2, rebirth.approvals.v2, etc.) will be added
  // as each phase lands. See Agentlets-Implementation-Plan.md §5
  // for the full taxonomy.
  {
    name: 'rebirth.shell.v2',
    label: 'Rebirth · UI v2 foundation (Phase 0)',
    description:
      'Enables the v2 design-system foundation: the /v2-playground ' +
      'reference page, all v2 components, and the redesigned shell ' +
      '(deep navy sidebar + white topbar with kill-switch + approvals + ⌘K + user). ' +
      'When OFF the rebirth surface is invisible — no nav links, no ' +
      'playground route, the v1 DashboardLayout renders. Toggle in browser ' +
      'console: localStorage.setItem(\'features.rebirth.shell.v2\', \'true\').',
    defaultOn: true,
    accessor: isRebirthShellEnabled,
    group: 'Rebirth (UI v2)',
  },
  {
    name: 'rebirth.overview.v2',
    label: 'Rebirth · Overview page (Phase 1)',
    description:
      'Renders the redesigned outcomes-first Overview page. Composition ' +
      'rule: only takes effect when rebirth.shell.v2 is also ON. With ' +
      'shell on but this OFF, the v1 OverviewPage renders inside the v2 ' +
      'chrome. Toggle: localStorage.setItem(\'features.rebirth.overview.v2\', \'true\').',
    defaultOn: true,
    accessor: isRebirthOverviewEnabled,
    group: 'Rebirth (UI v2)',
  },
  {
    name: 'rebirth.approvals.v2',
    label: 'Rebirth · Approvals queue (Phase 2)',
    description:
      'Renders the redesigned Approvals queue using the v2 ApprovalCard ' +
      'component (live SLA timer, urgency border, async approve/reject). ' +
      'Composition rule: requires rebirth.shell.v2 ON.',
    defaultOn: true,
    accessor: isRebirthApprovalsEnabled,
    group: 'Rebirth (UI v2)',
  },
  {
    name: 'rebirth.agentlets-inventory.v2',
    label: 'Rebirth · Agentlets inventory (Phase 3)',
    description:
      'Renders the unified Agentlets inventory across all four archetypes ' +
      '(AutoLet / Let / ExteLet / SysLet) with FacetRail filtering, v2 Table, ' +
      'and the universal Slideout for row triage. Composition rule: requires ' +
      'rebirth.shell.v2 ON. The full Agentlet detail page (Phase 4) is a ' +
      'separate flag — when this is ON but Phase 4 is OFF, the slideout ' +
      'still renders and "Open detail page" navigates to the v1 AgentsPage.',
    defaultOn: true,
    accessor: isRebirthAgentletsInventoryEnabled,
    group: 'Rebirth (UI v2)',
  },
  {
    name: 'rebirth.agentlet-detail.v2',
    label: 'Rebirth · Agentlet Detail (Phase 4)',
    description:
      'Renders the redesigned Agentlet Detail page at /agents/:id with the ' +
      'Build / Decide / Act phase tabs (plus a leading Registration tab for ' +
      'ExteLets), PhaseGate validation, KillSwitchButton header, and v2 ' +
      'composition of every modal/wizard. The page rolls out in three sub-' +
      'phases (4a Build / 4b Decide / 4c Act) but a single flag toggles all ' +
      'three for simplicity. Composition rule: requires rebirth.shell.v2 ON.',
    defaultOn: true,
    accessor: isRebirthAgentletDetailEnabled,
    group: 'Rebirth (UI v2)',
  },
  {
    name: 'rebirth.discovery.v2',
    label: 'Rebirth · Discovery (Phase 5)',
    description:
      'Renders the redesigned Agent Discovery (formerly Shadow Discovery) and ' +
      'Workflow Discovery pages with v2 Table + Slideout. The "Promote to ' +
      'ExteLet" CTA deep-links into the GEL registration flow. Composition rule.',
    defaultOn: true,
    accessor: isRebirthDiscoveryEnabled,
    group: 'Rebirth (UI v2)',
  },
  {
    name: 'rebirth.governance.v2',
    label: 'Rebirth · Protection Center (Phase 6)',
    description:
      'Renders the redesigned Protection Center landing + sub-views: Trust, ' +
      'Compliance, Data Flow, Audit Trail, Workflow Intelligence. Sub-views ' +
      'roll out incrementally — Trust + Compliance ship in Phase 6 with the ' +
      'remaining three landing in Phase 6 follow-ups. Composition rule.',
    defaultOn: true,
    accessor: isRebirthGovernanceEnabled,
    group: 'Rebirth (UI v2)',
  },
  { name: 'rebirth.policies.v2',       label: 'Rebirth · Policies (Phase 7)',      description: 'V2 Policies page with TabNav over the 8 existing tabs. Composition rule.',                                                          defaultOn: true, accessor: isRebirthPoliciesEnabled,     group: 'Rebirth (UI v2)' },
  { name: 'rebirth.skill-center.v2',   label: 'Rebirth · Skill Center (Phase 8)',  description: 'V2 Skill Center with v2 Table for the org-wide skill library. Composition rule.',                                                  defaultOn: true, accessor: isRebirthSkillCenterEnabled,  group: 'Rebirth (UI v2)' },
  { name: 'rebirth.build-section.v2',  label: 'Rebirth · Build section (Phase 9)', description: 'V2 chrome on Connectors (3 tabs), Agent Marketplace, Templates. Composition rule.',                                                defaultOn: true, accessor: isRebirthBuildSectionEnabled, group: 'Rebirth (UI v2)' },
  { name: 'rebirth.administer.v2',     label: 'Rebirth · Administer (Phase 10)',   description: 'V2 chrome on Admin Center (6 tabs incl. Inference), Team, Deployments, Protected Gateway. Composition rule.',                       defaultOn: true, accessor: isRebirthAdministerEnabled,   group: 'Rebirth (UI v2)' },
  { name: 'rebirth.system.v2',         label: 'Rebirth · System (Phase 11)',       description: 'V2 chrome on Intelligence Center + Feature Flags page. Super Admin only routes. Composition rule.',                                defaultOn: true, accessor: isRebirthSystemEnabled,       group: 'Rebirth (UI v2)' },

  // ── Left-nav sections ────────────────────────────────────────────
  // Each section flag toggles visibility of an entire group in the
  // dashboard sidebar. Defaults are ON for every section; a SuperAdmin
  // can hide an entire group from a tenant by flipping the flag off.
  // DashboardLayout.jsx reads these via `isNavSectionEnabled(key)`.
  {
    name: 'nav.operations',
    label: 'Nav · Operations',
    description:
      'Operations group in the left nav: Overview, Agent Lifecycle, Workflow ' +
      'Canvas, Approval Center. Hide this section to reduce surface area for ' +
      'read-only tenants or specialised installations.',
    defaultOn: true,
    accessor: navAccessor('operations'),
    group: 'Navigation',
  },
  {
    name: 'nav.manage',
    label: 'Nav · Manage',
    description:
      'Manage group in the left nav: Organizations, Connectors, Agents ' +
      '(marketplace), Team. Disable for tenants that do not need to manage ' +
      'their own connectors or org structure.',
    defaultOn: true,
    accessor: navAccessor('manage'),
    group: 'Navigation',
  },
  {
    name: 'nav.governance',
    label: 'Nav · Protection core',
    description:
      'Top-level Protection group: Protection Hub, Compliance Center, Policy ' +
      'Config. Hide when the tenant has no compliance / policy obligations ' +
      'and just wants the runtime surfaces.',
    defaultOn: true,
    accessor: navAccessor('governance'),
    group: 'Navigation',
  },
  {
    name: 'nav.discoveryGel',
    label: 'Nav · Discovery & GEL',
    description:
      'Discovery & GEL sub-group inside Protection: Agent Discovery, Workflow ' +
      'Discovery. Hide when the tenant has no shadow / discovered agents.',
    defaultOn: true,
    accessor: navAccessor('discoveryGel'),
    group: 'Navigation',
  },
  {
    name: 'nav.agentletsAgents',
    label: 'Nav · Agentlets Agents',
    description:
      'Agentlets Agents sub-group: Trust Center, Execution & Audit (for ' +
      'platform-native agents). Hide when the tenant only runs External Agents.',
    defaultOn: true,
    accessor: navAccessor('agentletsAgents'),
    group: 'Navigation',
  },
  {
    name: 'nav.externalAgents',
    label: 'Nav · External Agents',
    description:
      'External Agents sub-group: Trust Center, Execution & Audit, ' +
      'Registrations, External Agent Frameworks. Hide when the tenant only ' +
      'runs Agentlets-native agents.',
    defaultOn: true,
    accessor: navAccessor('externalAgents'),
    group: 'Navigation',
  },
  {
    name: 'nav.governedGateway',
    label: 'Nav · Protected Gateway',
    description:
      'Protected Gateway sub-group: API Keys, Gateway Dashboard. Hide when ' +
      'the tenant has not adopted the gateway tier yet.',
    defaultOn: true,
    accessor: navAccessor('governedGateway'),
    group: 'Navigation',
  },
  {
    name: 'nav.settings',
    label: 'Nav · Settings',
    description:
      'Settings group in the left nav: Inference Controls, Template Center. ' +
      'Hide for tenants that should not configure model routing or templates.',
    defaultOn: true,
    accessor: navAccessor('settings'),
    group: 'Navigation',
  },
  {
    name: 'nav.intelligence',
    label: 'Nav · Intelligence Center',
    description:
      'Intelligence Center (System group, SuperAdmin-only). The route also ' +
      'enforces SuperAdmin role; this flag adds a second toggle so SuperAdmins ' +
      'can hide the surface entirely from their own nav (e.g. for screen ' +
      'recordings, customer demos, or air-gapped builds).',
    defaultOn: true,
    accessor: navAccessor('intelligence'),
    group: 'Navigation',
  },

  // ── AI builders / capabilities ───────────────────────────────────
  // Each flag toggles a specific AI feature. Defaults are ON; flipping
  // off lets a tenant disable that capability tenant-wide for
  // compliance, budget, or air-gap reasons. The Intelligence Center's
  // AI Model Configuration tab still chooses WHICH model each enabled
  // feature uses; these flags decide whether the feature exists at all.
  {
    name: 'ai.canvasBuilder',
    label: 'AI · Canvas Builder',
    description:
      'AI Workflow Builder on /canvas, /canvas-v2 and /canvas-v3 — describe ' +
      'a workflow in natural language and the canvas materialises. Calls ' +
      'aiCanvasBuild → POST /api/v1/canvas/ai/build.',
    defaultOn: true,
    accessor: aiAccessor('canvasBuilder'),
    group: 'AI builders',
  },
  {
    name: 'ai.canvasDebugger',
    label: 'AI · Canvas Debugger',
    description:
      'Whole-workflow Debug action in the AI bar — analyses the current ' +
      'graph, returns quickFixes, nodeAnalysis, flowIssues. Calls ' +
      'aiCanvasDebug → POST /api/v1/canvas/ai/debug. Powers the chip rail and ' +
      'the explainer panel under the hood.',
    defaultOn: true,
    accessor: aiAccessor('canvasDebugger'),
    group: 'AI builders',
  },
  {
    name: 'ai.explainerPanel',
    label: 'AI · Node Explainer (Tier-S #1)',
    description:
      'AI Explainer panel at the top of the right Task tab on /canvas-v3. ' +
      'Auto-fetches a Business + Technical breakdown of the selected node, ' +
      'with type-aware prompts (condition / fork / loop / sub_workflow / ' +
      'approval_gate / signal_wait / terminate / etc.). Reuses ' +
      'aiCanvasDebug under the hood. Disable when air-gapped.',
    defaultOn: true,
    accessor: aiAccessor('explainerPanel'),
    group: 'AI builders',
  },
  {
    name: 'ai.recommendationChips',
    label: 'AI · Recommendation chips (Tier-S #2)',
    description:
      'Amber chip rail above the AI Workflow Builder input — surfaces the top ' +
      '3 quickFixes from the most recent Debug analysis as one-click ' +
      'apply-this-fix buttons. Each chip → focused aiCanvasBuild call. ' +
      'Promotes AI from "chat tool" to "active assistant".',
    defaultOn: true,
    accessor: aiAccessor('recommendationChips'),
    group: 'AI builders',
  },
  {
    name: 'ai.templateGenerator',
    label: 'AI · Template Generator',
    description:
      'AI Template Builder in the Template Center — generate reusable ' +
      'workflow templates from a prompt. Calls aiGenerateTemplate → POST ' +
      '/api/v1/templates/ai/generate.',
    defaultOn: true,
    accessor: aiAccessor('templateGenerator'),
    group: 'AI builders',
  },
  {
    name: 'ai.testRunChat',
    label: 'AI · Test-run agent chat',
    description:
      'Conversational AI in the agent test-run flow — chat with an agent ' +
      'before publishing to verify behaviour. Calls testRunChat → POST ' +
      '/api/v1/agents/{id}/test-run/chat.',
    defaultOn: true,
    accessor: aiAccessor('testRunChat'),
    group: 'AI builders',
  },
  {
    name: 'ai.intelligenceChat',
    label: 'AI · Intelligence Center Chat',
    description:
      'Operator chat inside the Intelligence Center — ask about errors, ' +
      'connectors, policy, platform health. Calls intelligenceChat → ' +
      'POST /api/intelligence/chat. SuperAdmin-only surface.',
    defaultOn: true,
    accessor: aiAccessor('intelligenceChat'),
    group: 'AI builders',
  },
  {
    name: 'ai.policyExtractor',
    label: 'AI · Policy Extractor',
    description:
      'AI-driven enforcement-pattern extraction from uploaded policy / ' +
      'compliance documents. Calls triggerExtraction → POST ' +
      '/api/v1/policy-config/extract. Disable for tenants that build ' +
      'enforcement maps by hand.',
    defaultOn: true,
    accessor: aiAccessor('policyExtractor'),
    group: 'AI builders',
  },

  // ── Worker runtime ───────────────────────────────────────────────
  // The `workerHosting` flag gates the layer-2 "Run on Agentlets
  // Workers (managed)" route. When OFF, worker_task nodes only show
  // the Conductor-style polling story (default, fully supported). When
  // ON, the right-panel Worker tab also surfaces a Hosting Mode toggle
  // letting the user flip a node onto the Temporal OSS-backed managed pool.
  // Default OFF until the backend Temporal OSS queue lands; the toggle is
  // a UX placeholder ahead of that work.
  {
    name: 'workerHosting',
    label: 'Runtime · Agentlets-managed workers (Temporal OSS-backed)',
    description:
      'Surfaces the "Run on Agentlets Workers (managed)" hosting toggle on ' +
      'worker_task nodes. With this flag OFF, worker_task is a Conductor-' +
      'compatible polling story only — customers run their own worker ' +
      'processes against the agentlets backend\'s Conductor-shaped poll ' +
      'endpoints. With it ON, the Worker tab adds a per-node Hosting Mode ' +
      'switch that, when flipped to "Agentlets-managed", routes execution ' +
      'through our Temporal OSS-backed runtime so the customer doesn\'t operate ' +
      'the worker fleet. Default OFF until the Temporal OSS pool is wired.',
    defaultOn: false,
    accessor: isWorkerHostingEnabled,
    group: 'Runtime',
  },

  // ── Runtime · backend (cluster-wide, restart-required) ───────────
  // These are NOT browser-local. They live in the Spring environment
  // (docker-compose / application.yml / env vars), apply to every tenant
  // on the cluster, and change only with an application restart. They
  // render here as informational rows (backendManaged) so /flags is the
  // single Platform-Admin gatekeeper that documents what runtime knobs
  // exist and how they're set — without pretending a UI button flips them.
  {
    name: 'backend.identity.caEnabled',
    label: 'Runtime · Platform-as-CA SVID issuance (ID-7)',
    description:
      'Activates PlatformCaWorkloadApiClient (the @Primary SpireWorkloadApiClient): ' +
      'the platform mints its own short-lived X.509-SVIDs per agent on demand — ' +
      'scalable to any fleet size, no SPIRE infra, no per-agent registration. When ' +
      'unset/false the NoOp default stays and Agent Identity remains in preview ' +
      '(zero regression). For production, also set agentlets.identity.ca.root-cert-path ' +
      'and .root-key-path to a Vault/KMS-sourced root; otherwise a dev root auto-' +
      'generates under /tmp/agentlets-ca. Mutually exclusive with the file-SPIRE ' +
      'path (agentlets.spiffe.svid.dir).',
    defaultOn: false,
    accessor: () => false,
    group: 'Runtime',
    backendManaged: true,
    // ID-7 — Platform-Admin-only server-backed toggle. ON/OFF persist the desired
    // value via /api/v1/platform/runtime-flags and apply on the next restart.
    serverToggle: true,
    flagKey: 'agentlets.identity.ca.enabled',
    propertyName: 'agentlets.identity.ca.enabled',
    defaultValue: 'false  (staging compose sets true)',
    restartImpact: 'Application Restart required.',
  },
  {
    name: 'backend.demo.simulator',
    label: 'Runtime · Demo Simulation endpoints',
    description:
      'Scripted demo endpoints (/api/v1/demo/simulate-*) that publish FABRICATED protection ' +
      'events — DPG scan detections, policy-gate decisions, probation transitions and ' +
      'audit-hash-chain seals — onto a live workflow run’s enforcement topic. They exist so a ' +
      'product demo can run without real connectors or Temporal. OFF by default and it should ' +
      'stay off in any customer environment: an operator watching the enforcement view cannot ' +
      'tell a simulated seal from a real one. Even when ON, the endpoints require Platform ' +
      'Admin, verify the target run is inside the caller’s tenant scope, and write an audit ' +
      'record naming whoever fired them.',
    defaultOn: false,
    accessor: () => false,
    group: 'Runtime',
    backendManaged: true,
    serverToggle: true,
    flagKey: 'agentlets.demo.simulator.enabled',
    propertyName: 'agentlets.demo.simulator.enabled',
    defaultValue: 'false',
    restartImpact: 'Takes effect immediately (read live on every request).',
  },
  {
    name: 'backend.ai.routerEnabled',
    label: 'Runtime · AI Model Config provider router',
    description:
      'Master switch for the AI Model Config provider router. When OFF (default), ' +
      'every internal LLM utility call (Auto-Create intent extraction, AI workflow ' +
      'builder) goes straight to the managed Claude path — byte-for-byte the current ' +
      'behaviour. When ON, those calls honour the per-scope model routing configured ' +
      'in Admin Center → AI Models (self-hosted / SLM endpoints, fallback, air-gapped). ' +
      'Cognitive inference + eval generation use the separate Agent Inference provider ' +
      'layer and are unaffected by this switch.',
    defaultOn: false,
    accessor: () => false,
    group: 'Runtime',
    backendManaged: true,
    // Unlike the CA flag, LlmRouter reads this via PlatformRuntimeFlagService on
    // each call, so the toggle takes effect live (no restart needed).
    serverToggle: true,
    flagKey: 'agentlets.ai.router.enabled',
    propertyName: 'agentlets.ai.router.enabled',
    defaultValue: 'false',
    restartImpact: 'Takes effect immediately (read live per call).',
  },
  {
    name: 'backend.gateway.enforce',
    label: 'Runtime · Protected Gateway — Enforcement',
    description:
      'Master switch for the Protected API plane. ON by default — but this alone blocks nothing: ' +
      'a request is only blocked when its customer’s plan is set to ENFORCE (per-customer default ' +
      'is OBSERVE). So the gateway is “always enforcing, each customer opts in” — set ENFORCE per ' +
      'customer in Customers → Plan & Entitlements. Denied requests then return 403 (scope/entitlement) ' +
      'or 429 (rate limit). Flip this OFF to disable gateway enforcement globally (kill switch).',
    defaultOn: true,
    accessor: () => true,
    group: 'Governed API Gateway',
    backendManaged: true,
    serverToggle: true,
    flagKey: 'agentlets.gateway.api-plane.enforce',
    propertyName: 'agentlets.gateway.api-plane.enforce',
    defaultValue: 'true',
    restartImpact: 'Takes effect immediately (read live per gateway request).',
  },
  {
    name: 'backend.gateway.rateLimit',
    label: 'Runtime · Protected Gateway — Rate limiting',
    description:
      'Turns per-key rate limiting on for the Protected API plane. When ON and the customer is ' +
      'enforcing, requests over its configured RPM/burst get 429 with Retry-After + ' +
      'X-RateLimit-* headers; in observe it only logs. The memory-vs-Postgres backend is a ' +
      'deploy setting (agentlets.gateway.rate-limit.backend).',
    defaultOn: false,
    accessor: () => false,
    group: 'Governed API Gateway',
    backendManaged: true,
    serverToggle: true,
    flagKey: 'agentlets.gateway.rate-limit.enabled',
    propertyName: 'agentlets.gateway.rate-limit.enabled',
    defaultValue: 'false',
    restartImpact: 'Takes effect immediately (read live per gateway request).',
  },
  {
    name: 'backend.gateway.rateLimitDistributed',
    label: 'Runtime · Protected Gateway — Distributed rate limiter (Postgres)',
    description:
      'OFF = per-replica in-memory limiter (fast, approximate across replicas). ON = Postgres ' +
      'token bucket so a key’s RPM/burst holds across ALL replicas (one shared bucket row). ' +
      'Default follows the agentlets.gateway.rate-limit.backend deploy setting.',
    defaultOn: false,
    accessor: () => false,
    group: 'Governed API Gateway',
    backendManaged: true,
    serverToggle: true,
    flagKey: 'agentlets.gateway.rate-limit.distributed',
    propertyName: 'agentlets.gateway.rate-limit.distributed',
    defaultValue: 'false',
    restartImpact: 'Takes effect immediately (read live per gateway request).',
  },
  {
    name: 'backend.gateway.authenticate',
    label: 'Runtime · Protected Gateway — Authenticate as key owner',
    description:
      'When ON, a valid + ALLOWED gateway key / OAuth token authenticates the request AS THE KEY ' +
      'OWNER for customer-reachable routes — the platform’s RBAC then runs as that user, so a key ' +
      'can actually call the API surface it is scoped for. OFF (default) = the gateway governs and ' +
      'observes but admits no one to the broad plane. Admin/internal routes are never reachable by a key.',
    defaultOn: false,
    accessor: () => false,
    group: 'Governed API Gateway',
    backendManaged: true,
    serverToggle: true,
    flagKey: 'agentlets.gateway.api-plane.authenticate',
    propertyName: 'agentlets.gateway.api-plane.authenticate',
    defaultValue: 'false',
    restartImpact: 'Takes effect immediately (read live per gateway request).',
  },
  {
    name: 'backend.gateway.oauthAs',
    label: 'Runtime · Protected MCP — Per-user OAuth authorization server',
    description:
      'Master switch for per-user access on the protected MCP surface. When ON, an application connects ' +
      'by signing each person in (sign-in + consent), so every governed tool call carries that ' +
      'person’s identity and their per-user access grants apply. OFF (default) = connections ' +
      'authenticate at the agent level and per-user grants are not applied. Flip OFF to disable the ' +
      'OAuth surface entirely (kill switch).',
    defaultOn: false,
    accessor: () => false,
    group: 'Governed MCP · Per-user OAuth',
    backendManaged: true,
    serverToggle: true,
    flagKey: 'agentlets.gateway.oauth.as.enabled',
    propertyName: 'agentlets.gateway.oauth.as.enabled',
    defaultValue: 'false',
    restartImpact: 'Takes effect immediately (read live on each OAuth request).',
  },
  {
    name: 'backend.gateway.mcpSurfaceScope',
    label: 'Runtime · Protected MCP — Connect once across your agents',
    description:
      'When ON, a person who signs an application in connects to the whole protected surface at once and ' +
      'sees the tools of every agent they’re allowed to use — each tool call is checked against their ' +
      'per-user grants for that agent. OFF (default) = each connection is tied to a single agent. ' +
      'Requires per-user access (the switch above) to be ON.',
    defaultOn: false,
    accessor: () => false,
    group: 'Governed MCP · Per-user OAuth',
    backendManaged: true,
    serverToggle: true,
    flagKey: 'agentlets.gateway.mcp.surface-scope.enabled',
    propertyName: 'agentlets.gateway.mcp.surface-scope.enabled',
    defaultValue: 'false',
    restartImpact: 'Takes effect immediately (read live on each authorization).',
  },
  {
    name: 'backend.gateway.oauthPublicBaseUrl',
    label: 'Runtime · Protected MCP — Public base URL (OAuth issuer)',
    description:
      'The deployment\'s externally-visible base URL (scheme://host, no trailing slash). It is the ' +
      'OAuth authorization-server issuer and the base for the authorize / token / registration / ' +
      'protected-resource URLs. Set this for a single-issuer or tunnel deployment, where the request ' +
      'reaches the app on an internal host (behind cloudflared or an ingress that rewrites Host) that ' +
      'is not the URL the external client used — when set, it wins over the request host. Leave it ' +
      'blank for multi-tenant per-subdomain hosting, where each request\'s own host becomes its issuer.',
    defaultOn: false,
    accessor: () => false,
    group: 'Governed MCP · Per-user OAuth',
    backendManaged: true,
    // Value flag (not a boolean toggle) — renders a text field via ServerTextFlagRow.
    serverText: true,
    flagKey: 'agentlets.gateway.oauth.public-base-url',
    propertyName: 'agentlets.gateway.oauth.public-base-url',
    placeholder: 'https://your-tunnel-or-public-host',
    restartImpact: 'Takes effect immediately (read live on each OAuth request).',
  },
  {
    name: 'backend.ratelimit.oauthToken',
    label: 'Runtime · OAuth token endpoint — rate limit',
    description:
      'Requests per minute per client IP to the OAuth token endpoint (authorization-code ' +
      'redemption + refresh rotation). Platform-wide, per source IP. Blank reverts to the ' +
      'built-in default (60).',
    defaultOn: false,
    accessor: () => false,
    group: 'Governed MCP · Per-user OAuth',
    backendManaged: true,
    serverText: true,
    flagKey: 'agentlets.ratelimit.oauth-token',
    propertyName: 'agentlets.ratelimit.oauth-token',
    placeholder: '60',
    restartImpact: 'Takes effect within ~15 seconds (read live, short-cached) — no restart.',
  },
  {
    name: 'backend.ratelimit.oauthAuthorize',
    label: 'Runtime · OAuth authorize endpoint — rate limit',
    description:
      'Requests per minute per client IP to the OAuth authorize/consent endpoint. Platform-wide, ' +
      'per source IP. Blank reverts to the built-in default (30).',
    defaultOn: false,
    accessor: () => false,
    group: 'Governed MCP · Per-user OAuth',
    backendManaged: true,
    serverText: true,
    flagKey: 'agentlets.ratelimit.oauth-authorize',
    propertyName: 'agentlets.ratelimit.oauth-authorize',
    placeholder: '30',
    restartImpact: 'Takes effect within ~15 seconds (read live, short-cached) — no restart.',
  },
  {
    name: 'backend.ratelimit.oauthRegister',
    label: 'Runtime · OAuth client registration — rate limit',
    description:
      'Requests per minute per client IP to dynamic client registration. Platform-wide, per source ' +
      'IP; keep low (registration is infrequent). Blank reverts to the built-in default (10).',
    defaultOn: false,
    accessor: () => false,
    group: 'Governed MCP · Per-user OAuth',
    backendManaged: true,
    serverText: true,
    flagKey: 'agentlets.ratelimit.oauth-register',
    propertyName: 'agentlets.ratelimit.oauth-register',
    placeholder: '10',
    restartImpact: 'Takes effect within ~15 seconds (read live, short-cached) — no restart.',
  },
  {
    name: 'backend.ratelimit.windowMs',
    label: 'Runtime · Rate-limit window (ms)',
    description:
      'Sliding-window length in milliseconds for the auth/OAuth rate limits. Blank reverts to the ' +
      'built-in default (60000 = 1 minute).',
    defaultOn: false,
    accessor: () => false,
    group: 'Governed MCP · Per-user OAuth',
    backendManaged: true,
    serverText: true,
    flagKey: 'agentlets.ratelimit.window-ms',
    propertyName: 'agentlets.ratelimit.window-ms',
    placeholder: '60000',
    restartImpact: 'Takes effect within ~15 seconds (read live, short-cached) — no restart.',
  },
  {
    name: 'backend.identity.caSvidTtl',
    label: 'Runtime · Platform CA — SVID TTL',
    description:
      'Validity of each minted X.509-SVID leaf, as an ISO-8601 duration. Short by ' +
      'design (SPIFFE SVIDs rotate frequently); the rotation scheduler re-mints ' +
      'before expiry. Only meaningful when Platform-as-CA issuance is enabled above.',
    defaultOn: true,
    accessor: () => true,
    group: 'Runtime',
    backendManaged: true,
    propertyName: 'agentlets.identity.ca.svid-ttl',
    defaultValue: 'PT1H',
    restartImpact: 'Application Restart required.',
  },

  // ── Backend-managed (read-only) ──────────────────────────────────
  // These flags live in the Spring environment (application.yml or env
  // vars). Toggling them in the UI is intentionally not supported — they
  // affect every tenant on the cluster and applying them safely requires
  // a backend restart with proper change-management. The /flags page
  // renders these as informational rows so operators can see what knobs
  // exist and where to find them, without giving the false impression
  // that flipping a button does anything.
  //
  // The accessor returns the BUILT-IN DEFAULT — the actual live value
  // depends on the running backend's config and would require a server
  // probe to read. We label the row "default <value>" so it's clear
  // we're showing the documented default, not the realised value.

  // ── Workflow AI Analyst (Phase-3) ────────────────────────────────
  {
    name: 'backend.workflowAi.enabled',
    label: 'Workflow AI Analyst — master switch',
    description:
      'Master enable/disable for the Workflow Intelligence → AI Analyst tab. ' +
      'When false, the tab returns a "disabled" envelope to the UI and never ' +
      'invokes an LLM provider. Use to suppress AI calls entirely in air-' +
      'gapped or compliance-restricted environments.',
    defaultOn: true,
    accessor: () => true,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.workflow-ai.enabled',
    defaultValue: 'true',
    restartImpact: 'Backend restart required.',
  },
  {
    name: 'backend.workflowAi.model',
    label: 'Workflow AI Analyst — model identifier',
    description:
      'Which LLM model the AI Analyst calls. Per-tenant overrides in the AI ' +
      'Model Configuration UI take precedence; this property is the cluster-' +
      'wide fallback used when no tenant has configured a "workflow_ai" row. ' +
      'Default mirrors the connector registry\'s "recommended" Anthropic snapshot.',
    defaultOn: true,
    accessor: () => true,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.workflow-ai.model',
    defaultValue: 'claude-sonnet-4-6',
    restartImpact: 'Backend restart required (tenant overrides via UI take effect immediately). Use GET /api/v1/model-config/probe-anthropic to discover IDs accessible to your specific Anthropic key.',
  },
  {
    name: 'backend.workflowAi.providerType',
    label: 'Workflow AI Analyst — provider hint',
    description:
      'Provider hint passed to LlmExecutionService when no tenant LlmProvider ' +
      'is configured. Currently only "anthropic" is supported end-to-end; ' +
      '"openai" will work for tenants that have configured an OpenAI provider.',
    defaultOn: true,
    accessor: () => true,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.workflow-ai.provider-type',
    defaultValue: 'anthropic',
    restartImpact: 'Backend restart required.',
  },
  {
    name: 'backend.workflowAi.cacheTtlSeconds',
    label: 'Workflow AI Analyst — cache TTL',
    description:
      'How long the analyst caches a generated analysis (keyed by content ' +
      'hash of the briefing JSON). Operators iterating on a stress test ' +
      'should keep this short; static post-mortem environments can raise it ' +
      'to reduce LLM bills.',
    defaultOn: true,
    accessor: () => true,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.workflow-ai.cache-ttl-seconds',
    defaultValue: '300',
    restartImpact: 'Backend restart required.',
  },
  {
    name: 'backend.workflowAi.maxTokens',
    label: 'Workflow AI Analyst — max output tokens',
    description:
      'Hard cap on tokens the model can emit. 2048 fits the strict-JSON ' +
      'output schema with comfortable headroom; raise if findings/recs are ' +
      'getting truncated, lower to cap costs.',
    defaultOn: true,
    accessor: () => true,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.workflow-ai.max-tokens',
    defaultValue: '8192',
    restartImpact: 'Backend restart required.',
  },
  {
    name: 'backend.workflowAi.temperature',
    label: 'Workflow AI Analyst — sampling temperature',
    description:
      'Low by design — analytical precision over creative variance. Don\'t ' +
      'raise above ~0.4 for production tuning advice.',
    defaultOn: true,
    accessor: () => true,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.workflow-ai.temperature',
    defaultValue: '0.2',
    restartImpact: 'Backend restart required.',
  },

  // ── Workflow Sweeper (Phase-2) ───────────────────────────────────
  {
    name: 'backend.sweeper.enabled',
    label: 'Workflow Sweeper — master switch',
    description:
      'Detects pathological workflow_node_executions rows: stuck-in-queue ' +
      '(created but never started), stalled-running (started but never ' +
      'completed), and orphaned (parent run terminal but node not completed). ' +
      'Disable in dev / when running fixtures that intentionally leave node ' +
      'rows in-flight.',
    defaultOn: true,
    accessor: () => true,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.sweeper.enabled',
    defaultValue: 'true',
    restartImpact: 'Backend restart required.',
  },
  {
    name: 'backend.sweeper.fixedDelayMs',
    label: 'Workflow Sweeper — sweep cadence',
    description:
      'How often the sweeper runs. 5 minutes is a good default — fast enough ' +
      'to catch a stuck worker pool before queue depth hits the dashboard, ' +
      'slow enough not to add measurable DB load.',
    defaultOn: true,
    accessor: () => true,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.sweeper.fixed-delay-ms',
    defaultValue: '300000  (5 min)',
    restartImpact: 'Backend restart required.',
  },
  {
    name: 'backend.sweeper.stuckQueueThresholdMinutes',
    label: 'Workflow Sweeper — stuck-in-queue threshold',
    description:
      'A node sitting in-queue this long without a worker pickup is ' +
      'considered stuck. Tune up if you legitimately have long-tail ' +
      'scheduled work; tune down if you want faster operator alerts.',
    defaultOn: true,
    accessor: () => true,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.sweeper.stuck-queue-threshold-minutes',
    defaultValue: '10',
    restartImpact: 'Backend restart required.',
  },
  {
    name: 'backend.sweeper.stalledRunningThresholdMinutes',
    label: 'Workflow Sweeper — stalled-running threshold',
    description:
      'A node running this long without completing is considered stalled. ' +
      'Should be ≥ the largest activity timeout × 2 so legitimately ' +
      'long-running activities aren\'t classified as stalled.',
    defaultOn: true,
    accessor: () => true,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.sweeper.stalled-running-threshold-minutes',
    defaultValue: '60',
    restartImpact: 'Backend restart required.',
  },
  {
    name: 'backend.sweeper.autoFixStuck',
    label: 'Workflow Sweeper — auto-fix stuck rows',
    description:
      'When ON the sweeper marks stuck-in-queue rows as failed with a clear ' +
      'reason. OFF by default — first deploy should observe counts before ' +
      'enabling auto-remediation.',
    defaultOn: false,
    accessor: () => false,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.sweeper.auto-fix-stuck',
    defaultValue: 'false',
    restartImpact: 'Backend restart required.',
  },
  {
    name: 'backend.sweeper.autoFixStalled',
    label: 'Workflow Sweeper — auto-fix stalled rows',
    description:
      'When ON the sweeper marks stalled-running rows as failed. OFF by ' +
      'default — observe counts first, then enable.',
    defaultOn: false,
    accessor: () => false,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.sweeper.auto-fix-stalled',
    defaultValue: 'false',
    restartImpact: 'Backend restart required.',
  },
  {
    name: 'backend.sweeper.autoFixOrphaned',
    label: 'Workflow Sweeper — auto-fix orphaned rows',
    description:
      'When ON the sweeper marks orphaned rows as failed so the UI doesn\'t ' +
      'keep showing them as in-flight forever. OFF by default.',
    defaultOn: false,
    accessor: () => false,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.sweeper.auto-fix-orphaned',
    defaultValue: 'false',
    restartImpact: 'Backend restart required.',
  },
  {
    name: 'backend.sweeper.maxRowsPerCategory',
    label: 'Workflow Sweeper — per-run row cap',
    description:
      'Hard cap on rows mutated per category per sweep. Prevents a one-time ' +
      'drift event from triggering a 500k-row UPDATE; operator can re-run ' +
      'the sweeper manually if more remain.',
    defaultOn: true,
    accessor: () => true,
    group: 'Backend operations',
    backendManaged: true,
    propertyName: 'agentlets.sweeper.max-rows-per-category',
    defaultValue: '5000',
    restartImpact: 'Backend restart required.',
  },

  // ── Redesign (brand palette) ─────────────────────────────────────
  // Mutually-exclusive palette presets that re-bind the --v2-* tokens
  // (styles/redesign-themes.css) via <html data-redesign>. Precedence
  // when more than one is on: dark > foundations > trust. Reload the
  // app after toggling so the boot-time apply runs.
  {
    name: 'redesign.foundations',
    label: 'Redesign · A — Live-site light (recommended)',
    description:
      'Matches the published prove7.ai light site: white canvas + functional ' +
      'status colors, with the Prove7 brand chrome — navy (#0a1d44) sidebar, ' +
      'royal-navy (#103372) active state, navy primary action, and the steel ' +
      'Trust accent (replaces violet) with a navy->steel gauge ramp. Recommended ' +
      'brand option. Mutually exclusive with the other palettes (dark wins if both on).',
    defaultOn: true,
    accessor: isRedesignFoundationsEnabled,
    group: 'Redesign (brand palette)',
  },
  {
    name: 'redesign.dark',
    label: 'Redesign · B — Full dark theme',
    description:
      'Re-themes every surface to the marketing site\'s dark navy + white ' +
      '(#060c16 canvas, #0e2244 cards, white text). Status colors are kept ' +
      'but brightened for dark. Highest-impact option — re-binds all surface, ' +
      'text, border, action and shadow tokens. Takes precedence over the ' +
      'other Redesign palettes when multiple are on.',
    defaultOn: false,
    accessor: isRedesignDarkEnabled,
    group: 'Redesign (brand palette)',
  },
  {
    name: 'redesign.trust',
    label: 'Redesign · C — Trust coloring only',
    description:
      'Smallest slice: the current light product is unchanged except the ' +
      'Trust accent switches from violet to the brand steel ramp. Use to ' +
      'align just the most brand-relevant surface (Trust gauge / tiers / ' +
      'Agent Trust Score). Overridden by foundations or dark if those are on.',
    defaultOn: false,
    accessor: isRedesignTrustEnabled,
    group: 'Redesign (brand palette)',
  },
];

/**
 * Return the raw stored string for a flag, or null if unset. Useful when
 * the /flags admin page needs to distinguish "explicitly off" from
 * "never set" (which for `codeTab` means ON-by-default).
 *
 * @param {string} name
 * @returns {string|null}
 */
export function getRawFlag(name) {
  return readFlag(name);
}
